'use client';

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { SiteBuilder } from '@/components/admin/site-builder/SiteBuilder';
import { api } from '@/lib/api';
import { parseTenantDetail, parseTenantTheme } from '@/lib/platform/summary';
import { usePlatformSummary } from '@/pages/platform/usePlatformSummary';

const PAGE_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Platform-only site builder. The platform operator never opens the agency
 * desk to edit the website: the platform API opens the tenant database by slug.
 */
export function AgencySiteBuilderPage() {
  const slug = useParams().slug ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('page');
  const pageSlug = requested && PAGE_SLUG.test(requested) ? requested : 'home';
  const [revision, setRevision] = useState(0);
  const { overview, error } = usePlatformSummary('Could not load this agency.');
  const tenant = overview?.tenants.find((row) => row.slug === slug) ?? null;
  const [profileReady, setProfileReady] = useState(false);
  const [profile, setProfile] = useState<{
    phone?: string;
    email?: string;
    whatsapp?: string;
    address?: string;
    logoUrl?: string;
  }>({});

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setProfileReady(false);
    void (async () => {
      try {
        const [detailPayload, themePayload] = await Promise.all([
          api<unknown>(`/api/platform/tenants/${slug}`),
          api<unknown>(`/api/platform/tenants/${slug}/theme`).catch(() => null),
        ]);
        if (cancelled) return;
        const detail = parseTenantDetail(detailPayload);
        let logoUrl = '';
        if (themePayload) {
          try {
            logoUrl = parseTenantTheme(themePayload).logoUrl || '';
          } catch {
            logoUrl = '';
          }
        }
        setProfile({
          phone: detail.profile?.phone,
          email: detail.profile?.email,
          whatsapp: detail.profile?.whatsapp,
          address: detail.profile?.address,
          logoUrl: logoUrl || undefined,
        });
      } catch {
        // Branding is best-effort; the builder still opens.
      } finally {
        if (!cancelled) setProfileReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const openPage = (nextSlug: string) => {
    setRevision((current) => current + 1);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextSlug === 'home') next.delete('page');
      else next.set('page', nextSlug);
      return next;
    });
  };

  if (error) {
    return (
      <div className="flex h-dvh items-center justify-center bg-shell p-6 text-center">
        <div className="max-w-md space-y-3">
          <p className="text-sm font-semibold text-danger">{error}</p>
          <Link to={`/super-admin/agencies/${slug}`} className="inline-flex rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">
            Back to agency
          </Link>
        </div>
      </div>
    );
  }

  if (!tenant || !profileReady) {
    return (
      <div className="flex h-dvh items-center justify-center bg-shell text-sm text-muted">
        Loading the site builder…
      </div>
    );
  }

  return (
    <SiteBuilder
      key={`${pageSlug}:${revision}:${profile.logoUrl ?? ''}`}
      agency={{
        slug: tenant.slug,
        name: tenant.name,
        customDomain: tenant.customDomain,
        phone: profile.phone,
        email: profile.email,
        whatsapp: profile.whatsapp,
        address: profile.address,
        logoUrl: profile.logoUrl,
      }}
      pageSlug={pageSlug}
      onSelectPage={openPage}
    />
  );
}
